import { useEffect, useState } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { IS_WEB } from '@/utils/platform';
import {
  allowScreenCaptureAsync,
  preventScreenCaptureAsync,
} from 'expo-screen-capture';
import constants from '@/config/constants';
import { logError } from '@/utils/logHandling';

export const usePrivacy = (isAuthenticated: boolean): boolean => {
  const [isPrivate, setIsPrivate] = useState(false);

  // On Android, the OS captures the recent-apps thumbnail during onPause() at the
  // native level — before the JS AppState event fires. FLAG_SECURE (set by
  // preventScreenCaptureAsync) tells Android to show a blank screen in the task
  // switcher instead. Only needed when screenshotPreventionEnabled is false,
  // because ScreenshotPrevention already sets FLAG_SECURE globally in that case.
  useEffect(() => {
    if (
      Platform.OS !== 'android' ||
      constants.screenshotPreventionEnabled ||
      !isAuthenticated
    ) {
      return;
    }
    preventScreenCaptureAsync('usePrivacy').catch(() => {
      logError('Failed to prevent screen capture');
    });
    return () => {
      allowScreenCaptureAsync('usePrivacy').catch(() => {
        logError('Failed to allow screen capture');
      });
    };
  }, [isAuthenticated]);

  const handleAppStateChange = (nextState: AppStateStatus) => {
    if (!isAuthenticated || IS_WEB) {
      setIsPrivate(false);
      return;
    }

    if (nextState === 'background' || nextState === 'inactive') {
      setIsPrivate(true);
    } else if (nextState === 'active') {
      setIsPrivate(false);
    }
  };

  useEffect(() => {
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, [isAuthenticated]);

  return isPrivate;
};
